import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useVenue } from "../context/VenueContext";
import { logOut } from "../services/authService";
import { loginWithSpotify } from "../services/spotifyService";

import TapScreen from "./TapScreen";
import HomeScreen from "./HomeScreen";
import QueueScreen from "./QueueScreen";
import SearchScreen from "./SearchScreen";
import NowPlayingScreen from "./NowPlayingScreen";
import ProfileScreen from "./ProfileScreen";
import VenueUsersScreen from "./VenueUsersScreen";
import ChatScreen from "./ChatScreen";
import ConnectionsScreen from "./ConnectionsScreen";

export default function MainApp({ spotifyToken, setSpotifyToken, onLogout }) {
  const { user, profile, clearGuestUser, isGuest } = useAuth();
  const { venueId, joinVenue, leaveVenue, isVenueMode, setIsVenueMode, initPlayer } = useVenue();
  const [screen, setScreen] = useState("tap");
  const [chatWithUser, setChatWithUser] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [notification, setNotification] = useState(null);

  // Initialize Spotify player — runs whenever token or venue mode changes
  useEffect(() => {
    const token = spotifyToken || localStorage.getItem("spotify_token");
    if (token && isVenueMode) {
      console.log("🎵 MainApp: Initializing player with token");
      initPlayer(token);
    }
  }, [spotifyToken, isVenueMode, initPlayer]);

  // Also initialize if token arrives after venue mode is already set
  useEffect(() => {
    const token = localStorage.getItem("spotify_token");
    if (token && isVenueMode && !spotifyToken) {
      console.log("🎵 MainApp: Found saved token, initializing player");
      initPlayer(token);
    }
  }, [isVenueMode, spotifyToken, initPlayer]);

  // Restore venue after Spotify OAuth redirect
  useEffect(() => {
    const savedVenueId = localStorage.getItem("tapped_venue_id");
    const venueMode = localStorage.getItem("tapped_mode") === "venue";
    if (savedVenueId && venueMode && !venueId) {
      joinVenue(savedVenueId);
      setIsVenueMode(true);
    }
  }, [venueId, joinVenue, setIsVenueMode]);

  useEffect(() => {
    if (venueId) setScreen("home");
    else setScreen("tap");
  }, [venueId]);

  const showNotif = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 2500);
  };

  const handleLogout = async () => {
    if (isGuest) {
      clearGuestUser();
    } else {
      await logOut();
    }
    leaveVenue();
    onLogout();
  };

  const handleVenueJoin = async (id, venueMode) => {
    await joinVenue(id);
    if (venueMode) {
      setIsVenueMode(true);
      localStorage.setItem("tapped_venue_id", id);
      localStorage.setItem("tapped_mode", "venue");
      const savedToken = localStorage.getItem("spotify_token");
      if (!savedToken) {
        loginWithSpotify("venue");
      } else {
        initPlayer(savedToken);
      }
    }
  };

  const openChat = (otherUser) => {
    setChatWithUser(otherUser);
    setScreen("chat");
  };

  const openProfile = (uid) => {
    setViewingProfile(uid);
    setScreen("profile");
  };

  // Always show these core social tabs
  const socialTabs = [
    { id: "connections", icon: "🤝", label: "Connect" },
    { id: "chat", icon: "💬", label: "Messages" },
  ];

  // Venue music/playback tabs
  const venueTabs = [
    { id: "home", icon: "🏠", label: "Home" },
    { id: "search", icon: "🔍", label: "Search" },
    { id: "queue", icon: "🎵", label: "Queue" },
    { id: "nowplaying", icon: "▶️", label: "Playing" },
  ];

  // People at venue — separated from playback tabs, left of Connect
  const hereTab = [
    { id: "venueusers", icon: "👥", label: "Here" },
  ];

  // Pre-venue tabs shown when not inside a venue
  const preTabs = [
    { id: "tap", icon: "📲", label: "Tap In" },
    { id: "profile", icon: "👤", label: "Profile" },
  ];

  // Build the nav: venue tabs | Here | social tabs (when in venue)
  const navItems = venueId
    ? [...venueTabs, ...hereTab, ...socialTabs]
    : [...preTabs, ...socialTabs];

  const handleNavClick = (id) => {
    setViewingProfile(null);
    setChatWithUser(null);
    setScreen(id);
  };

  const renderScreen = () => {
    switch (screen) {
      case "tap":
        return <TapScreen onJoin={handleVenueJoin} onNotif={showNotif} />;
      case "home":
        return <HomeScreen setScreen={setScreen} onNotif={showNotif} onOpenProfile={openProfile} />;
      case "queue":
        return <QueueScreen setScreen={setScreen} onNotif={showNotif} />;
      case "search":
        return <SearchScreen setScreen={setScreen} onNotif={showNotif} />;
      case "nowplaying":
        return <NowPlayingScreen setScreen={setScreen} onNotif={showNotif} />;
      case "venueusers":
        return <VenueUsersScreen setScreen={setScreen} onOpenChat={openChat} onOpenProfile={openProfile} />;
      case "profile":
        return <ProfileScreen uid={viewingProfile || user?.uid} onBack={() => { setViewingProfile(null); setScreen(venueId ? "home" : "tap"); }} onOpenChat={openChat} onNotif={showNotif} onLogout={handleLogout} />;
      case "chat":
        return <ChatScreen otherUser={chatWithUser} onBack={() => { setChatWithUser(null); setScreen(venueId ? "venueusers" : "connections"); }} />;
      case "connections":
        return <ConnectionsScreen onOpenChat={openChat} onOpenProfile={openProfile} onNotif={showNotif} />;
      default:
        return <TapScreen onJoin={handleVenueJoin} onNotif={showNotif} />;
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: "#0A0A0A", minHeight: "100vh", color: "#fff" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .app-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; padding: 0 40px; height: 64px; display: flex; justify-content: space-between; align-items: center; background: rgba(10,10,10,0.95); backdrop-filter: blur(10px); border-bottom: 1px solid rgba(255,255,255,0.06); }
        .app-nav-logo { font-family: 'DM Serif Display', serif; font-size: 28px; color: #fff; letter-spacing: -1px; cursor: pointer; }
        .app-nav-links { display: flex; align-items: center; gap: 2px; overflow-x: auto; flex: 1; justify-content: center; min-width: 0; margin: 0 12px; }
        .app-nav-link { padding: 8px 10px; border-radius: 100px; border: none; background: none; color: rgba(255,255,255,0.5); font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; white-space: nowrap; flex-shrink: 0; }
        .app-nav-link:hover { color: #fff; background: rgba(255,255,255,0.06); }
        .app-nav-link.active { color: #fff; background: rgba(255,255,255,0.1); }
        .app-nav-right { display: flex; align-items: center; gap: 12px; }
        .app-nav-avatar { cursor: pointer; transition: transform 0.2s; display: flex; align-items: center; gap: 8px; padding: 6px 14px 6px 6px; border-radius: 100px; border: 1.5px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.04); }
        .app-nav-avatar:hover { border-color: rgba(255,255,255,0.3); background: rgba(255,255,255,0.08); }
        .app-page { padding-top: 64px; min-height: 100vh; max-width: 960px; margin: 0 auto; }
        .bottom-nav { display: none; }
        .bottom-nav-item { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; flex: 1; min-width: 0; padding: 8px 2px 10px; cursor: pointer; border: none; background: none; font-family: 'DM Sans', sans-serif; transition: color 0.2s; }
        .bottom-nav-icon { font-size: 18px; line-height: 1; }
        .bottom-nav-label { font-size: 9px; font-weight: 600; letter-spacing: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; color: rgba(255,255,255,0.4); transition: color 0.2s; }
        .bottom-nav-item.active .bottom-nav-label { color: #ffffff; }
        .bottom-nav-divider { width: 1px; height: 28px; background: rgba(255,255,255,0.08); flex-shrink: 0; align-self: center; }
        .notif { position: fixed; top: 80px; left: 50%; transform: translateX(-50%); background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); color: #ffffff; padding: 10px 20px; border-radius: 100px; font-size: 13px; font-weight: 500; white-space: nowrap; z-index: 200; animation: notifIn 0.3s cubic-bezier(0.32, 0.72, 0, 1); backdrop-filter: blur(10px); font-family: 'DM Sans', sans-serif; }
        .notif.error { background: rgba(255,107,107,0.15); border-color: rgba(255,107,107,0.3); color: #ff6b6b; }
        @keyframes notifIn { from { opacity: 0; transform: translateX(-50%) translateY(-8px) scale(0.95); } to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); } }
        @media (max-width: 768px) {
          .app-nav { padding: 0 16px; }
          .app-nav-links { display: none; }
          .app-nav-avatar-name { display: none; }
          .app-nav-avatar { padding: 4px; }
          .app-page { padding-bottom: 70px; }
          .bottom-nav {
            display: flex;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 100;
            align-items: stretch;
            justify-content: space-around;
            padding: 0 4px;
            padding-bottom: env(safe-area-inset-bottom, 0px);
            background: rgba(10,10,10,0.95);
            backdrop-filter: blur(20px);
            border-top: 1px solid rgba(255,255,255,0.08);
          }
        }
      `}</style>

      {/* TOP NAV — links hidden on mobile, logo + avatar only */}
      <nav className="app-nav">
        <div className="app-nav-logo" onClick={() => { setViewingProfile(null); setScreen(venueId ? "home" : "tap"); }}>tapped</div>
        <div className="app-nav-links">
          {navItems.map((item, i) => {
            const prev = navItems[i - 1];
            // | pipe only before "Here"
            const showPipe = venueId && item.id === "venueusers";
            // · dot between all other adjacent items (not before a pipe)
            const showDot = i > 0 && !showPipe;
            return (
              <React.Fragment key={item.id}>
                {showPipe && (
                  <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 16, margin: "0 2px", userSelect: "none" }}>|</span>
                )}
                {showDot && (
                  <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 14, margin: "0 2px", userSelect: "none" }}>·</span>
                )}
                <button
                  className={`app-nav-link ${screen === item.id ? "active" : ""}`}
                  onClick={() => handleNavClick(item.id)}
                >
                  {item.label}
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div className="app-nav-right">
          <div className="app-nav-avatar" onClick={() => { setViewingProfile(null); setScreen("profile"); }}>
            {profile?.photoURL
              ? <img src={profile.photoURL} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
              : <span style={{ background: "rgba(255,255,255,0.1)", borderRadius: "50%", width: 28, height: 28, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>👤</span>
            }
            <span className="app-nav-avatar-name" style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>{profile?.displayName?.split(" ")[0] || "Profile"}</span>
          </div>
        </div>
      </nav>

      {notification && <div className={`notif ${notification.type === "error" ? "error" : ""}`}>{notification.msg}</div>}

      <div className="app-page">
        {renderScreen()}
      </div>

      {/* BOTTOM TAB BAR — mobile only */}
      <nav className="bottom-nav" aria-label="Main navigation">
        {navItems.map((item) => {
          const showDivider = venueId && (item.id === "venueusers" || item.id === "connections");
          const isActive = screen === item.id;
          return (
            <React.Fragment key={item.id}>
              {showDivider && <div className="bottom-nav-divider" />}
              <button
                type="button"
                className={`bottom-nav-item ${isActive ? "active" : ""}`}
                onClick={() => handleNavClick(item.id)}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="bottom-nav-icon" style={{ opacity: isActive ? 1 : 0.7 }}>{item.icon}</span>
                <span className="bottom-nav-label">{item.label}</span>
              </button>
            </React.Fragment>
          );
        })}
      </nav>
    </div>
  );
}
